import { useCallback, useEffect, useMemo, useState } from 'react';

import { apiService } from '../../services';
import { useCompanyStore } from '../../store/companyStore';
import type {
  SubEstipulanteCoverageItem,
  SubEstipulanteInfoField,
} from '../../types/vida/cadastralMaintenance/subEstipulanteInfoTypes';
import type {
  ApiCoverageItem,
  ApiStipulatorItem,
  ApiUniformCapitalItem,
} from '../../types/vida/contract/contractType';
import type { ListaSubestipulanteItem } from '../../types/vida/subEstipulante/subEstipulanteType';
import { getSortedSubestipulantesList } from '../../utils/subestipulanteUtil';
import {
  buildSubEstipulanteInfoSections,
  mapAgeLimitsToRegrasAdesao,
  mapCoverageItemsToDisplay,
  mapUniformCapitalToRegras,
  type SubEstipulanteAgeLimits,
  type SubEstipulanteInfoSections,
} from '../../utils/subEstipulanteInfoUtil';

const IDENT_ABERTO_POR_COBERTURA = 'S';

export type SubestipulantePanelView = 'info';

const emptyInfoData: SubEstipulanteInfoSections = {
  cadastrais: [],
  contato: [],
  endereco: [],
  comerciais: [],
  coberturas: [],
  limitesCapital: [],
  regrasAdesao: [],
  regrasCapital: [],
};

export interface UseSubestipulantesListStateReturn {
  identSubestipulante: string;
  hasSearched: boolean;
  subestipulantePanelView: SubestipulantePanelView | null;
  showSubestipulantePanel: boolean;
  selectedSubestipulante: ListaSubestipulanteItem | null;
  subestipulantesList: ListaSubestipulanteItem[];
  numeroApoliceSubestipulantes: string | undefined;
  handleViewSubestipulante: (subestipulante: ListaSubestipulanteItem) => void;
  handleInfoSubEstipulante: (subestipulante: ListaSubestipulanteItem) => void;
  handleBackToSubestipulantesList: () => void;
  handleBackFromSubestipulantePanel: () => void;
  resetSubestipulantePanel: () => void;
  setIdentSubestipulante: (value: string) => void;
  setHasSearched: (value: boolean) => void;
}

export interface UseSubEstipulanteInfoDetailReturn {
  infoData: SubEstipulanteInfoSections;
  isLoading: boolean;
  error: string | null;
}

export interface UseSubEstipulanteResumoReturn {
  selectedSubestipulante: ListaSubestipulanteItem | null;
  isOpen: boolean;
  coberturas: SubEstipulanteCoverageItem[];
  regrasELimites: SubEstipulanteInfoField[];
  isLoading: boolean;
  error: string | null;
  open: (subestipulante: ListaSubestipulanteItem) => void;
  close: () => void;
}

export interface UseSubEstipulanteInfoOptions {
  infoSubestipulante?: ListaSubestipulanteItem | null;
  resumoSubestipulante?: ListaSubestipulanteItem | null;
  resumoIsOpen?: boolean;
}

export interface UseSubEstipulanteInfoReturn {
  list: UseSubestipulantesListStateReturn;
  info: UseSubEstipulanteInfoDetailReturn;
  resumo: UseSubEstipulanteResumoReturn;
}

export function useSubEstipulanteInfo(
  options?: UseSubEstipulanteInfoOptions,
): UseSubEstipulanteInfoReturn {
  const [identSubestipulante, setIdentSubestipulante] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [subestipulantePanelView, setSubestipulantePanelView] = useState<SubestipulantePanelView | null>(null);
  const [resumoTarget, setResumoTarget] = useState<ListaSubestipulanteItem | null>(null);

  const selectedCompany = useCompanyStore((state) => state.selectedCompany);
  const subestipulantesSource = selectedCompany?.subestipulantes;

  const subestipulantesList = useMemo(
    () => getSortedSubestipulantesList(subestipulantesSource, false),
    [subestipulantesSource],
  );

  useEffect(() => {
    const allowed = new Set(
      subestipulantesList.map((item) => String(item.identSubestipulanteOut)),
    );

    if (identSubestipulante && !allowed.has(identSubestipulante)) {
      setIdentSubestipulante('');
      setHasSearched(false);
    }
  }, [subestipulantesList, identSubestipulante]);

  const selectedSubestipulante = useMemo(() => {
    if (!identSubestipulante || !subestipulantesSource?.length) {
      return null;
    }
    return subestipulantesSource.find(
      (item) => String(item.identSubestipulanteOut) === identSubestipulante,
    ) ?? null;
  }, [identSubestipulante, subestipulantesSource]);

  const numeroApoliceSubestipulantes = selectedCompany?.policySelection?.numeroApolice ?? undefined;

  const resetSubestipulantePanel = useCallback(() => {
    setSubestipulantePanelView(null);
  }, []);

  const handleViewSubestipulante = useCallback((subestipulante: ListaSubestipulanteItem) => {
    resetSubestipulantePanel();
    setIdentSubestipulante(String(subestipulante.identSubestipulanteOut));
    setHasSearched(true);
  }, [resetSubestipulantePanel]);

  const handleInfoSubEstipulante = useCallback((subestipulante: ListaSubestipulanteItem) => {
    setHasSearched(false);
    setIdentSubestipulante(String(subestipulante.identSubestipulanteOut));
    setSubestipulantePanelView('info');
  }, []);

  const handleBackToSubestipulantesList = useCallback(() => {
    setHasSearched(false);
    setIdentSubestipulante('');
  }, []);

  const handleBackFromSubestipulantePanel = useCallback(() => {
    resetSubestipulantePanel();
    setIdentSubestipulante('');
  }, [resetSubestipulantePanel]);

  const showSubestipulantePanel = subestipulantePanelView !== null;

  const usesExternalResumo = options?.resumoSubestipulante !== undefined
    || options?.resumoIsOpen !== undefined;

  const infoSubestipulante = options?.infoSubestipulante
    ?? (subestipulantePanelView === 'info' ? selectedSubestipulante : null);

  const resumoSubestipulante = usesExternalResumo
    ? (options?.resumoSubestipulante ?? null)
    : resumoTarget;
  const resumoIsOpen = usesExternalResumo
    ? (options?.resumoIsOpen ?? false)
    : resumoTarget !== null;

  const openResumo = useCallback((subestipulante: ListaSubestipulanteItem) => {
    setResumoTarget(subestipulante);
  }, []);

  const closeResumo = useCallback(() => {
    setResumoTarget(null);
  }, []);

  const [isInfoLoading, setIsInfoLoading] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [estipulanteItem, setEstipulanteItem] = useState<ApiStipulatorItem | null>(null);
  const [infoCoverageItems, setInfoCoverageItems] = useState<ApiCoverageItem[]>([]);
  const [infoUniformCapitalItems, setInfoUniformCapitalItems] = useState<ApiUniformCapitalItem[]>([]);
  const [infoAgeLimits, setInfoAgeLimits] = useState<SubEstipulanteAgeLimits>({});

  const [isResumoLoading, setIsResumoLoading] = useState(false);
  const [resumoError, setResumoError] = useState<string | null>(null);
  const [resumoCoverageItems, setResumoCoverageItems] = useState<ApiCoverageItem[]>([]);
  const [resumoUniformCapitalItems, setResumoUniformCapitalItems] = useState<ApiUniformCapitalItem[]>([]);
  const [resumoAgeLimits, setResumoAgeLimits] = useState<SubEstipulanteAgeLimits>({});

  useEffect(() => {
    if (!infoSubestipulante?.identContratoOut || !infoSubestipulante.identSubestipulanteOut) {
      setEstipulanteItem(null);
      setInfoCoverageItems([]);
      setInfoUniformCapitalItems([]);
      setInfoAgeLimits({});
      setInfoError(null);
      setIsInfoLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadSubEstipulanteInfo = async () => {
      setIsInfoLoading(true);
      setInfoError(null);

      const apoliceContext = selectedCompany?.apoliceContext;
      const numeroApoliceRaw = selectedCompany?.policySelection?.numeroApolice?.trim();
      const parsedNumApolice = numeroApoliceRaw ? parseInt(numeroApoliceRaw, 10) : undefined;
      const identSubestipulanteOut = infoSubestipulante.identSubestipulanteOut;

      const [
        estipulanteResult,
        coverageResult,
        capitalUniformeResult,
        ageMinResult,
        ageFirstResult,
        ageOtherResult,
      ] = await Promise.allSettled([
        apiService.contracts.getEstipulanteData(
          infoSubestipulante.identContratoOut,
          apoliceContext?.codEmpresaApol,
          apoliceContext?.codSucursalApol,
          apoliceContext?.codRamoApol,
          apoliceContext?.codprodutoApol,
          parsedNumApolice !== undefined && !Number.isNaN(parsedNumApolice)
            ? parsedNumApolice
            : undefined,
        ),
        apiService.contracts.getCoverage(
          infoSubestipulante.identContratoOut,
          identSubestipulanteOut,
        ),
        apiService.contracts.getCapitalUniforme(
          identSubestipulanteOut,
          IDENT_ABERTO_POR_COBERTURA,
        ),
        apiService.contracts.getAgeLimitMin(identSubestipulanteOut),
        apiService.contracts.getAgeLimitFirstInvoice(identSubestipulanteOut),
        apiService.contracts.getAgeLimitOtherInvoices(identSubestipulanteOut),
      ]);

      if (cancelled) {
        return;
      }

      const errors: string[] = [];

      if (estipulanteResult.status === 'fulfilled') {
        setEstipulanteItem(estipulanteResult.value?.retorno?.[0] ?? null);
      } else {
        setEstipulanteItem(null);
        errors.push('Erro ao carregar dados cadastrais do subestipulante');
      }

      if (coverageResult.status === 'fulfilled') {
        setInfoCoverageItems(coverageResult.value?.pagina?.content ?? []);
      } else {
        setInfoCoverageItems([]);
        errors.push('Erro ao carregar coberturas do subestipulante');
      }

      if (capitalUniformeResult.status === 'fulfilled') {
        setInfoUniformCapitalItems(capitalUniformeResult.value?.pagina?.content ?? []);
      } else {
        setInfoUniformCapitalItems([]);
        errors.push('Erro ao carregar capital uniforme do subestipulante');
      }

      const nextAgeLimits: SubEstipulanteAgeLimits = {};

      if (ageMinResult.status === 'fulfilled') {
        nextAgeLimits.minimumAge = ageMinResult.value?.retorno?.[0]?.valorLimiteIdadeMinima;
      } else {
        errors.push('Erro ao carregar idade mínima do subestipulante');
      }

      if (ageFirstResult.status === 'fulfilled') {
        nextAgeLimits.maxAgeFirstInvoice =
          ageFirstResult.value?.retorno?.[0]?.valorLimiteIdadeMaximaPrimeiraFatura;
      } else {
        errors.push('Erro ao carregar idade máxima para primeira fatura');
      }

      if (ageOtherResult.status === 'fulfilled') {
        nextAgeLimits.maxAgeOtherInvoices =
          ageOtherResult.value?.retorno?.[0]?.valorIdadeMaximaDemaisFaturas;
      } else {
        errors.push('Erro ao carregar idade máxima para demais faturas');
      }

      setInfoAgeLimits(nextAgeLimits);
      setInfoError(errors.length > 0 ? errors.join('. ') : null);
      setIsInfoLoading(false);
    };

    void loadSubEstipulanteInfo();

    return () => {
      cancelled = true;
    };
  }, [infoSubestipulante, selectedCompany]);

  useEffect(() => {
    if (!resumoIsOpen || !resumoSubestipulante?.identContratoOut || !resumoSubestipulante.identSubestipulanteOut) {
      setResumoCoverageItems([]);
      setResumoUniformCapitalItems([]);
      setResumoAgeLimits({});
      setResumoError(null);
      setIsResumoLoading(false);
      return undefined;
    }

    let cancelled = false;
    const identSubestipulanteOut = resumoSubestipulante.identSubestipulanteOut;

    const loadResumo = async () => {
      setIsResumoLoading(true);
      setResumoError(null);

      const [coverageResult, capitalUniformeResult, ageMinResult] = await Promise.allSettled([
        apiService.contracts.getCoverage(
          resumoSubestipulante.identContratoOut,
          identSubestipulanteOut,
        ),
        apiService.contracts.getCapitalUniforme(
          identSubestipulanteOut,
          IDENT_ABERTO_POR_COBERTURA,
        ),
        apiService.contracts.getAgeLimitMin(identSubestipulanteOut),
      ]);

      if (cancelled) {
        return;
      }

      const errors: string[] = [];

      if (coverageResult.status === 'fulfilled') {
        setResumoCoverageItems(coverageResult.value.pagina?.content ?? []);
      } else {
        setResumoCoverageItems([]);
        errors.push('Erro ao carregar coberturas');
      }

      if (capitalUniformeResult.status === 'fulfilled') {
        setResumoUniformCapitalItems(capitalUniformeResult.value.pagina?.content ?? []);
      } else {
        setResumoUniformCapitalItems([]);
        errors.push('Erro ao carregar capital uniforme');
      }

      const nextAgeLimits: SubEstipulanteAgeLimits = {};

      if (ageMinResult.status === 'fulfilled') {
        nextAgeLimits.minimumAge = ageMinResult.value.retorno?.[0]?.valorLimiteIdadeMinima;
      } else {
        errors.push('Erro ao carregar idade mínima');
      }

      setResumoAgeLimits(nextAgeLimits);
      setResumoError(errors.length > 0 ? errors.join('. ') : null);
      setIsResumoLoading(false);
    };

    void loadResumo();

    return () => {
      cancelled = true;
    };
  }, [resumoIsOpen, resumoSubestipulante]);

  const infoData = useMemo(
    () => (
      infoSubestipulante
        ? buildSubEstipulanteInfoSections(
          infoSubestipulante,
          estipulanteItem,
          infoCoverageItems,
          infoUniformCapitalItems,
          infoAgeLimits,
        )
        : emptyInfoData
    ),
    [infoSubestipulante, estipulanteItem, infoCoverageItems, infoUniformCapitalItems, infoAgeLimits],
  );

  const coberturas = useMemo(
    () => mapCoverageItemsToDisplay(resumoCoverageItems, resumoUniformCapitalItems),
    [resumoCoverageItems, resumoUniformCapitalItems],
  );

  const regrasELimites = useMemo(
    () => [
      ...mapAgeLimitsToRegrasAdesao(resumoAgeLimits),
      ...mapUniformCapitalToRegras(resumoUniformCapitalItems),
    ],
    [resumoAgeLimits, resumoUniformCapitalItems],
  );

  return {
    list: {
      identSubestipulante,
      hasSearched,
      subestipulantePanelView,
      showSubestipulantePanel,
      selectedSubestipulante,
      subestipulantesList,
      numeroApoliceSubestipulantes,
      handleViewSubestipulante,
      handleInfoSubEstipulante,
      handleBackToSubestipulantesList,
      handleBackFromSubestipulantePanel,
      resetSubestipulantePanel,
      setIdentSubestipulante,
      setHasSearched,
    },
    info: {
      infoData,
      isLoading: isInfoLoading,
      error: infoError,
    },
    resumo: {
      selectedSubestipulante: resumoSubestipulante,
      isOpen: resumoIsOpen,
      coberturas,
      regrasELimites,
      isLoading: isResumoLoading,
      error: resumoError,
      open: openResumo,
      close: closeResumo,
    },
  };
}
